import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TrustPaymentsFxQuoteFx, TrustPaymentsKycData, TrustPaymentsKycFieldKey, trustPaymentsService } from "src/api";
import { Theme, useTheme } from "src/contexts/ThemeContext";
import { Button, ErrorAlert, Field, InfoField } from "src/components";
import { formatNumberToLocale, Rounds } from "src/utils";
import { ISO_COUNTRIES } from "src/utils/iso-countries";
import "./trustpayments-card-form.scss";

/** SecureTrading JS library — loaded from Trust's CDN on demand. */
const SECURE_TRADING_SDK_URL = "https://cdn.eu.trustpayments.com/js/latest/st.js";

interface SecureTradingResponseItem {
  errorcode?: string;
  errormessage?: string;
  errordata?: string[];
}

interface SecureTradingResponse {
  payload?: { response?: SecureTradingResponseItem[] };
  response?: SecureTradingResponseItem[];
  errorcode?: string;
  errormessage?: string;
  errordata?: string[];
}

interface ApplePayConfig {
  buttonStyle?: "white-outline" | "white" | "black";
  buttonText?: "plain" | "buy" | "donate" | "check-out" | "book" | "subscribe";
  buttonPlacement?: string;
  merchantId: string;
  paymentRequest: {
    countryCode: string;
    currencyCode: string;
    merchantCapabilities: string[];
    supportedNetworks: string[];
    total: { label: string; amount: string };
  };
}

interface GooglePayConfig {
  buttonOptions: {
    buttonRootNode: string;
    buttonColor?: "default" | "black" | "white";
    buttonType?: "plain" | "buy" | "donate" | "checkout";
    /** Pixel border-radius of the rendered Google Pay button. Google
     *  allows 0–100 here; we match the card-input radius (10px) so
     *  the button sits flush with the rest of the form. */
    buttonRadius?: number;
    /** `fill` makes the button span the container width; `static`
     *  uses Google's intrinsic sizing. Container width comes from
     *  `__wallet` (100%) so we always want fill. */
    buttonSizeMode?: "static" | "fill";
  };
  paymentRequest: {
    allowedPaymentMethods: Array<{
      parameters: {
        allowedAuthMethods: string[];
        allowedCardNetworks: string[];
      };
    }>;
    environment: "TEST" | "PRODUCTION";
    merchantInfo: { merchantId: string };
    transactionInfo: {
      countryCode: string;
      currencyCode: string;
      totalPriceStatus: "FINAL";
      totalPrice: string;
      totalPriceLabel?: string;
      checkoutOption?: "COMPLETE_IMMEDIATE_PURCHASE";
    };
  };
}

interface SecureTradingInstance {
  Components: (config?: {
    callbacks?: {
      onPaymentFormRendered?: () => void;
      onPaymentFormValidityChange?: (data: { isFormValid?: boolean }) => void;
    };
  }) => void;
  ApplePay?: (cfg: ApplePayConfig) => void;
  GooglePay?: (cfg: GooglePayConfig) => void;
}

type StStyles = Record<string, string | boolean>;

const getCssVariable = (name: string, fallback: string): string => {
  if (typeof window === "undefined") return fallback;
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
};

interface SecureTradingFactory {
  (config: {
    jwt: string;
    livestatus: 0 | 1;
    styles?: StStyles;
    submitCallback?: (data: SecureTradingResponse) => void;
    errorCallback?: (data: SecureTradingResponse) => void;
    disableNotification?: boolean;
    submitOnSuccess?: boolean;
    submitOnError?: boolean;
    submitOnCancel?: boolean;
    formId?: string;
    buttonId?: string;
  }): SecureTradingInstance;
}

// Apple Pay availability is a runtime browser check — Safari on
// iOS/macOS only, and the device must have a card provisioned. We
// only mount the container when both conditions hold so the SDK
// never reports an "unavailable" state into our DOM.
const isApplePayAvailable = (): boolean => {
  if (typeof window === "undefined") return false;
  const session = (window as unknown as { ApplePaySession?: { canMakePayments?: () => boolean } }).ApplePaySession;
  return !!(session?.canMakePayments && session.canMakePayments());
};

// Map our settle currency to a billing country for the wallet
// payment request. Apple/Google require an ISO2 country; the actual
// cardholder country comes from the wallet itself.
const walletCountryForCurrency = (currency: string): string => {
  const upper = currency.toUpperCase();
  if (upper === "EUR" || upper === "EURC") return "DE";
  return "US";
};

// Style overrides for Trust's iframed card-detail inputs. Keys mirror
// Trust's documented `styles` schema. Kept in the widget (not BE HTML)
// so the theme matches the host wallet chrome.
const buildStStyles = (): StStyles => {
  return {
    "background-color-body": "transparent",
    "space-inset-body": "0",
    "background-color-input": getCssVariable("--trustpayments-sdk-input-bg-color", "transparent"),
    "border-color-input": getCssVariable("--trustpayments-sdk-input-border-color", "rgba(0,0,0,0.12)"),
    "border-radius-input": "10px",
    "border-size-input": "1px",
    "color-input": getCssVariable("--trustpayments-sdk-input-text-color", "#1a1d2c"),
    "color-input-placeholder": getCssVariable("--trustpayments-sdk-input-placeholder-color", "rgba(0,0,0,0.35)"),
    "font-size-input": "15px",
    "space-inset-input": "12px 14px",
    "background-color-input:hover": getCssVariable("--trustpayments-sdk-input-bg-color", "transparent"),
    "border-color-input:hover": getCssVariable("--trustpayments-sdk-input-border-color", "rgba(0,0,0,0.12)"),
    "background-color-input:focus": getCssVariable("--trustpayments-sdk-input-bg-color", "transparent"),
    "outline-input": "none",
    "outline-input:focus": "none",
    "box-shadow-input": "none",
    "box-shadow-input:focus": getCssVariable("--trustpayments-sdk-input-focus-shadow", "0 0 0 2px rgba(91,233,168,0.16)"),
    "border-color-input:focus": getCssVariable("--trustpayments-sdk-input-border-color-focus", "#2cc7a8"),
    "background-color-input-error": getCssVariable("--trustpayments-sdk-input-error-bg-color", "rgba(235,80,72,0.06)"),
    "border-color-input-error": getCssVariable("--trustpayments-sdk-input-error-border-color", "#eb5048"),
    "color-input-error": getCssVariable("--trustpayments-sdk-input-error-text-color", "#c62828"),
    "color-label": getCssVariable("--trustpayments-sdk-label-color", "rgba(0,0,0,0.55)"),
    "font-size-label": "11px",
    "font-weight-label": "600",
    "text-transform-label": "uppercase",
    "display-asterisk": false,
    "color-error": getCssVariable("--trustpayments-sdk-message-error-color", "#c62828"),
    "font-size-message": "11.5px",
  };
};

declare global {
  interface Window {
    SecureTrading?: SecureTradingFactory;
  }
}

let sdkLoadPromise: Promise<void> | null = null;

const loadSecureTradingSdk = (): Promise<void> => {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.SecureTrading) return Promise.resolve();
  if (sdkLoadPromise) return sdkLoadPromise;

  sdkLoadPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SECURE_TRADING_SDK_URL}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("SecureTrading SDK failed to load")));
      return;
    }
    const script = document.createElement("script");
    script.src = SECURE_TRADING_SDK_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("SecureTrading SDK failed to load"));
    document.head.appendChild(script);
  });

  return sdkLoadPromise;
};

const parseStResponse = (data: SecureTradingResponse): SecureTradingResponseItem[] => {
  if (data?.payload?.response && Array.isArray(data.payload.response)) {
    return data.payload.response;
  }
  if (Array.isArray(data?.response)) return data.response;
  if (Array.isArray(data)) return data as SecureTradingResponseItem[];
  if (data && data.errorcode !== undefined) {
    return [data as SecureTradingResponseItem];
  }
  return [];
};

const kycFieldLabel = (key: TrustPaymentsKycFieldKey): string => {
  if (key === "full_name") return "Full name";
  if (key === "date_of_birth") return "Date of birth";
  if (key === "country") return "Country";
  return key;
};

interface TrustPaymentsCardFormProps {
  sessionId: string;
  kycFieldsRequired: TrustPaymentsKycFieldKey[];
  onReset: () => void;
  /** Gross fiat charge (USD/EUR) — what Trust authorizes on the card.
   *  Echoed on the card step so the user sees the exact amount before
   *  entering card details. */
  sourceAmount: number;
  sourceCurrency: string;
  targetCurrency: string;
  sourceCurrencyPrecision: number;
  targetCurrencyPrecision: number;
  platformFee: number;
  feeCurrency: string;
  /** Stablecoin (USDC/EURC) credited after platform fee. */
  totalReceiveAmount: number;
  /** Locked FX quote — when present, breakdown headlines switch to the
   *  user's local currency (BRL etc.); base currency line stays hidden. */
  fxQuote?: TrustPaymentsFxQuoteFx | null;
}

// SecureTrading SDK mount. Renders Trust's iframe-backed card fields
// inline so the widget owns wrapping chrome; PCI scope stays SAQ-A-EP
// because the PAN inputs are still served by Trust's CDN.
const TrustPaymentsCardForm: React.FC<TrustPaymentsCardFormProps> = ({
  sessionId,
  kycFieldsRequired,
  onReset,
  sourceAmount,
  sourceCurrency,
  targetCurrency,
  sourceCurrencyPrecision,
  targetCurrencyPrecision,
  platformFee,
  feeCurrency,
  totalReceiveAmount,
  fxQuote,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [kycValues, setKycValues] = useState<TrustPaymentsKycData>({});
  const [status, setStatus] = useState<"loading" | "ready" | "success" | "error">("loading");
  const [showCardValidationHint, setShowCardValidationHint] = useState(false);
  // SDK form submit → 3DS round-trip can take several seconds; track
  // this so the PAY button shows a spinner instead of looking idle.
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Drives a friendlier overlay copy on soft user-side aborts (wallet/3DS cancel).
  const [errorVariant, setErrorVariant] = useState<"failed" | "cancelled">("failed");
  // Wallet capability is decided after session bootstrap: Apple Pay
  // needs both the BE flag AND a browser ApplePaySession; Google Pay
  // only needs the merchant id (SDK handles device gating itself).
  const [walletState, setWalletState] = useState<{ apple: boolean; google: boolean }>({
    apple: false,
    google: false,
  });
  const initializedRef = useRef(false);
  const cardFormValidRef = useRef<boolean | null>(null);
  const cardSubmitAttemptedRef = useRef(false);
  // Distinguishes "user actually tried to pay" from "SDK warmup
  // error". Wallet init (Apple/Google Pay) can fire errorCallback
  // before any user action — domain not yet registered, browser
  // blocks Payment Request API, etc. Without this guard those land
  // in the terminal "Payment failed" overlay even though no card
  // was ever charged.
  const paymentAttemptedRef = useRef(false);
  // SDK callbacks register once on init; ref keeps KYC input values
  // current in the closure.
  const kycValuesRef = useRef(kycValues);
  kycValuesRef.current = kycValues;
  // Terminal-state guard: Trust's SDK fires multiple responses per
  // AUTH (THREEDQUERY → AUTH → post-3DS). Once we've shown success or
  // error, ignore further callbacks so a late "info"-shaped errorcode
  // can't downgrade a confirmed success back into the form.
  const terminalRef = useRef<"success" | "error" | null>(null);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    let cancelled = false;

    const init = async () => {
      try {
        const [, session] = await Promise.all([loadSecureTradingSdk(), trustPaymentsService.getSession(sessionId)]);
        if (cancelled) return;
        if (!window.SecureTrading) {
          throw new Error("SecureTrading SDK unavailable");
        }
        const st = window.SecureTrading({
          jwt: session.jwt,
          livestatus: 0,
          styles: buildStStyles(),
          submitCallback: handleStResponse,
          errorCallback: handleStResponse,
          disableNotification: true,
          submitOnSuccess: false,
          submitOnError: false,
          submitOnCancel: false,
          formId: "st-form",
          buttonId: "trustpayments-submit",
        });
        st.Components({
          callbacks: {
            onPaymentFormRendered: () => {
              if (!cancelled) setStatus("ready");
            },
            onPaymentFormValidityChange: (data) => {
              const isValid = !!data?.isFormValid;
              cardFormValidRef.current = isValid;
              setShowCardValidationHint(cardSubmitAttemptedRef.current && !isValid);
            },
          },
        });

        // Wallet mounts run alongside the card form — they share the
        // same JWT + callbacks, so a successful Apple/Google Pay AUTH
        // lands in the same `submitCallback` → success overlay path
        // the card flow uses.
        const appleAvailable = !!session.apple_pay_enabled && !!session.site_reference && isApplePayAvailable();
        const googleAvailable = !!session.google_pay_merchant_id;
        // Wallet sheet must match what Trust JWT actually charges:
        // on FX that's local_currency + local_amount, otherwise the
        // route base USD/EUR + sourceAmount.
        const walletCurrency = fxQuote ? fxQuote.local_currency : sourceCurrency;
        const walletTotal = fxQuote ? fxQuote.local_amount : sourceAmount;
        const formattedTotal = walletTotal.toFixed(2);
        const wantsDarkButton = theme === Theme.Dark;

        // Wallet init must never crash the card flow. A synchronous
        // throw here (e.g., Apple Pay domain not registered, browser
        // blocks Payment Request API in the host iframe) used to
        // escape into the outer catch and convert the entire form
        // into the terminal "Payment failed" overlay — before any
        // card was even touched. Catch locally and just drop the
        // wallet button instead.
        let appleMounted = false;
        if (appleAvailable && st.ApplePay) {
          try {
            st.ApplePay({
              // Mirror Google Pay's `buttonColor` mapping below so
              // both wallet buttons share the same light/dark tone.
              // Apple's `white-outline` produced a near-white pill on
              // a dark host that visually clashed with Google Pay's
              // solid black.
              buttonStyle: wantsDarkButton ? "black" : "white",
              buttonText: "plain",
              buttonPlacement: "st-apple-pay",
              merchantId: session.site_reference as string,
              paymentRequest: {
                countryCode: walletCountryForCurrency(walletCurrency),
                currencyCode: walletCurrency,
                merchantCapabilities: ["supports3DS", "supportsCredit", "supportsDebit"],
                supportedNetworks: ["visa", "masterCard", "amex"],
                total: {
                  label: t("TRUSTPAYMENTS_DEPOSIT.WALLET_TOTAL_LABEL", {
                    defaultValue: "Wallet top-up",
                  }),
                  amount: formattedTotal,
                },
              },
            });
            appleMounted = true;
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("TrustPayments Apple Pay init failed:", e);
          }
        }

        let googleMounted = false;
        if (googleAvailable && st.GooglePay) {
          try {
            st.GooglePay({
              buttonOptions: {
                buttonRootNode: "st-google-pay",
                buttonColor: wantsDarkButton ? "black" : "white",
                buttonType: "plain",
                buttonRadius: 12,
                buttonSizeMode: "fill",
              },
              paymentRequest: {
                allowedPaymentMethods: [
                  {
                    parameters: {
                      allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
                      allowedCardNetworks: ["AMEX", "DISCOVER", "JCB", "MASTERCARD", "VISA"],
                    },
                  },
                ],
                environment: session.google_pay_environment ?? "TEST",
                merchantInfo: { merchantId: session.google_pay_merchant_id as string },
                transactionInfo: {
                  countryCode: walletCountryForCurrency(walletCurrency),
                  currencyCode: walletCurrency,
                  totalPriceStatus: "FINAL",
                  totalPrice: formattedTotal,
                  checkoutOption: "COMPLETE_IMMEDIATE_PURCHASE",
                },
              },
            });
            googleMounted = true;
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("TrustPayments Google Pay init failed:", e);
          }
        }

        setWalletState({ apple: appleMounted, google: googleMounted });
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : String(e);
        setErrorMessage(msg);
        setStatus("error");
      }
    };

    const handleStResponse = (data: SecureTradingResponse) => {
      if (terminalRef.current) return;
      setSubmitting(false);

      const responses = parseStResponse(data);
      const firstError = responses.find((r) => r.errorcode && r.errorcode !== "0");
      if (firstError) {
        // SDK / wallet warmup errors arrive on the same callback as
        // real card-charge failures. Until the user has actually
        // attempted a payment we treat them as non-terminal — the
        // card form stays interactive, the wallet button just won't
        // appear.
        if (!paymentAttemptedRef.current) {
          // eslint-disable-next-line no-console
          console.warn("TrustPayments pre-attempt error suppressed:", firstError);
          return;
        }
        const rawMsg = (firstError.errormessage || "").trim().toLowerCase();
        const isCancel = rawMsg === "cancel" || rawMsg === "cancelled" || rawMsg === "canceled";
        terminalRef.current = "error";
        if (isCancel) {
          setErrorVariant("cancelled");
          setErrorMessage(null);
        } else {
          setErrorVariant("failed");
          const extra = (firstError.errordata || []).filter(Boolean).join(", ");
          const msg = firstError.errormessage || "Payment failed";
          setErrorMessage(extra ? `${msg} — ${extra}` : msg);
        }
        setStatus("error");
        setShowCardValidationHint(false);
        return;
      }

      if (responses.length > 0) {
        // Persist KYC inputs; BE recomputes `kyc_fields_required` for
        // future sessions.
        const kycPayload: TrustPaymentsKycData = {};
        kycFieldsRequired.forEach((key) => {
          const value = kycValuesRef.current[key]?.trim();
          if (value) kycPayload[key] = value;
        });
        terminalRef.current = "success";
        const finalize = () => setStatus("success");
        if (Object.keys(kycPayload).length) {
          trustPaymentsService.updateSessionKyc({ session_id: sessionId, kyc_data: kycPayload }).finally(finalize);
        } else {
          finalize();
        }
      }
    };

    init();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const handleKycChange = (key: TrustPaymentsKycFieldKey, value: string) => {
    setKycValues((prev) => ({ ...prev, [key]: value }));
  };

  const isSuccess = status === "success";
  const isError = status === "error";
  const isTerminal = isSuccess || isError;

  return (
    <section
      className={
        `trustpayments-card-form trustpayments-card-form--${theme === Theme.Dark ? "dark" : "light"}` +
        (isTerminal ? " trustpayments-card-form--terminal" : "")
      }
    >
      {/* Always mounted so the SDK's iframe children survive into the
          terminal state — hidden via CSS, not unmounted. */}
      <div
        className={"trustpayments-card-form__shell" + (isTerminal ? " trustpayments-card-form__shell--hidden" : "")}
        aria-hidden={isTerminal}
      >
        <button type="button" className="trustpayments-card-form__back" onClick={onReset}>
          {t("TRUSTPAYMENTS_DEPOSIT.BACK", { defaultValue: "← Back" })}
        </button>

        <InfoField
          className="info-field--muted"
          label={t("TRUSTPAYMENTS_DEPOSIT.PLATFORM_FEE", {
            defaultValue: "Platform fee",
          })}
          value={`${formatNumberToLocale(platformFee, targetCurrencyPrecision, Rounds.HalfUp, true)} ${feeCurrency || targetCurrency}`}
        />

        {/* FX markup is baked into the local-currency price (`local_amount`),
            not shown as a separate line. */}
        <InfoField
          label={
            fxQuote
              ? t("TRUSTPAYMENTS_DEPOSIT.TOTAL_TO_PAY_LOCAL", {
                  currency: fxQuote.local_currency,
                  defaultValue: `You'll pay in ${fxQuote.local_currency}`,
                })
              : t("TRUSTPAYMENTS_DEPOSIT.TOTAL_TO_SPEND", {
                  defaultValue: "Total to spend",
                })
          }
          value={
            fxQuote
              ? `${formatNumberToLocale(fxQuote.local_amount, 2)} ${fxQuote.local_currency}`
              : `${formatNumberToLocale(sourceAmount, sourceCurrencyPrecision)} ${sourceCurrency}`
          }
        />

        <InfoField
          label={t("TRUSTPAYMENTS_DEPOSIT.TOTAL_TO_RECEIVE", {
            defaultValue: "Total to receive",
          })}
          value={`${formatNumberToLocale(totalReceiveAmount, targetCurrencyPrecision, Rounds.HalfUp, true)} ${targetCurrency}`}
        />

        {kycFieldsRequired.length > 0 && (
          <div className="trustpayments-card-form__kyc">
            {kycFieldsRequired.map((key) => {
              if (key === "date_of_birth") {
                return (
                  <label key={key} className="field">
                    <span className="field-label">{kycFieldLabel(key)}</span>
                    <input
                      type="date"
                      className="field-input"
                      value={kycValues[key] ?? ""}
                      onChange={(event) => handleKycChange(key, event.target.value)}
                      disabled={status !== "ready"}
                      max={new Date().toISOString().slice(0, 10)}
                    />
                  </label>
                );
              }
              if (key === "country") {
                return (
                  <label key={key} className="field">
                    <span className="field-label">{kycFieldLabel(key)}</span>
                    <select
                      className="field-input"
                      value={kycValues[key] ?? ""}
                      onChange={(event) => handleKycChange(key, event.target.value)}
                      disabled={status !== "ready"}
                    >
                      <option value="" disabled>
                        {t("TRUSTPAYMENTS_DEPOSIT.SELECT_COUNTRY", { defaultValue: "Select country" })}
                      </option>
                      {ISO_COUNTRIES.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </label>
                );
              }
              return (
                <Field
                  key={key}
                  label={kycFieldLabel(key)}
                  value={kycValues[key] ?? ""}
                  onChange={(value) => handleKycChange(key, value)}
                  disabled={status !== "ready"}
                />
              );
            })}
          </div>
        )}

        {/* Wallet buttons render inside Apple/Google-owned iframes via
            the SDK — placeholders MUST exist in the DOM before
            st.ApplePay/GooglePay run, so render them unconditionally
            and let an empty mount stay 0-height when the device or
            config doesn't activate that wallet. The divider is gated
            by `walletState` so it only appears once a wallet is
            actually live. */}
        <div
          className={
            "trustpayments-card-form__wallets" +
            (!walletState.apple && !walletState.google ? " trustpayments-card-form__wallets--inactive" : "")
          }
        >
          {/* Trust mounts wallet buttons in nested iframes — we can't
              attach onClick to the button itself, but a capture-phase
              click on the host container fires before the iframe
              takes focus. That's enough to flip the attempt flag so
              wallet-driven errors flow into the terminal overlay,
              while pre-click SDK/init errors stay silent. */}
          <div
            id="st-apple-pay"
            className="trustpayments-card-form__wallet"
            onClickCapture={() => {
              paymentAttemptedRef.current = true;
            }}
          />
          <div
            id="st-google-pay"
            className="trustpayments-card-form__wallet"
            onClickCapture={() => {
              paymentAttemptedRef.current = true;
            }}
          />
          {(walletState.apple || walletState.google) && (
            <div className="trustpayments-card-form__divider">
              <span>
                {t("TRUSTPAYMENTS_DEPOSIT.OR_PAY_WITH_CARD", {
                  defaultValue: "or pay with card",
                })}
              </span>
            </div>
          )}
        </div>

        <form
          id="st-form"
          className="trustpayments-card-form__form"
          aria-busy={status === "loading"}
          noValidate
          onSubmit={(e) => {
            // SDK intercepts and calls preventDefault internally; this
            // is a safety net for the brief window before `Components()`
            // wires up or if the user presses Enter while SDK is dead.
            if (status !== "ready") {
              e.preventDefault();
              return;
            }
            cardSubmitAttemptedRef.current = true;
            setShowCardValidationHint(cardFormValidRef.current === false);
            if (cardFormValidRef.current === false) {
              e.preventDefault();
              setSubmitting(false);
              return;
            }
            paymentAttemptedRef.current = true;
            setSubmitting(true);
          }}
        >
          <div
            className={
              "trustpayments-card-form__sdk-loader" + (status !== "loading" ? " trustpayments-card-form__sdk-loader--hidden" : "")
            }
            role="status"
            aria-hidden={status !== "loading"}
          >
            <span className="trustpayments-card-form__sdk-spinner" aria-hidden />
            <span>
              {t("TRUSTPAYMENTS_DEPOSIT.LOADING_CARD_FORM", {
                defaultValue: "Loading secure card form…",
              })}
            </span>
          </div>

          <div id="st-card-number" />
          <div className="trustpayments-card-form__row">
            <div id="st-expiration-date" />
            <div id="st-security-code" />
          </div>

          {showCardValidationHint ? (
            <p className="trustpayments-card-form__field-error">
              {t("TRUSTPAYMENTS_DEPOSIT.CARD_VALIDATION_HINT", {
                defaultValue: "Please fill in the highlighted card fields.",
              })}
            </p>
          ) : (
            <p className="trustpayments-card-form__hint">
              {t("TRUSTPAYMENTS_DEPOSIT.CARD_HINT", {
                defaultValue: "Enter your card details to complete the payment. Your data is processed securely.",
              })}
            </p>
          )}

          <Button
            id="trustpayments-submit"
            title={t("TRUSTPAYMENTS_DEPOSIT.PAY_SECURELY", {
              defaultValue: "Pay securely",
            })}
            className="deposit-button"
            type="submit"
            disabled={status !== "ready" || submitting}
            pending={status === "loading" || submitting}
            onClick={() => {
              cardSubmitAttemptedRef.current = true;
              setShowCardValidationHint(cardFormValidRef.current === false);
            }}
          />
        </form>

        <ErrorAlert message={errorMessage} />
      </div>

      {isSuccess && (
        <div className="trustpayments-card-form__overlay trustpayments-card-form--success">
          <div className="trustpayments-card-form__success-badge" aria-hidden />
          <h2 className="trustpayments-card-form__title">
            {t("TRUSTPAYMENTS_DEPOSIT.SUCCESS_TITLE", {
              defaultValue: "Payment initiated",
            })}
          </h2>
          <p className="trustpayments-card-form__text">
            {t("TRUSTPAYMENTS_DEPOSIT.SUCCESS_TEXT", {
              defaultValue: "Your payment is being processed and your wallet will be credited shortly. Thank you for your trust.",
            })}
          </p>
        </div>
      )}

      {isError && (
        <div className="trustpayments-card-form__overlay trustpayments-card-form--failed">
          <div className="trustpayments-card-form__failed-badge" aria-hidden />
          <h2 className="trustpayments-card-form__title">
            {errorVariant === "cancelled"
              ? t("TRUSTPAYMENTS_DEPOSIT.CANCELLED_TITLE", {
                  defaultValue: "Payment cancelled",
                })
              : t("TRUSTPAYMENTS_DEPOSIT.FAILED_TITLE", {
                  defaultValue: "Payment failed",
                })}
          </h2>
          <p className="trustpayments-card-form__text">
            {errorVariant === "cancelled"
              ? t("TRUSTPAYMENTS_DEPOSIT.CANCELLED_TEXT", {
                  defaultValue: "You closed the payment window before it completed. Your card was not charged — you can try again or pick a different method.",
                })
              : errorMessage ||
                t("TRUSTPAYMENTS_DEPOSIT.FAILED_TEXT", {
                  defaultValue: "Your card was not charged. You can try again with the same or a different card.",
                })}
          </p>
          <div className="trustpayments-card-form__failed-actions">
            <Button
              title={t("TRUSTPAYMENTS_DEPOSIT.TRY_AGAIN", {
                defaultValue: "Try again",
              })}
              className="deposit-button"
              onClick={onReset}
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default TrustPaymentsCardForm;
